import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import FormInput from "../Shared/FormInput";
import FormAlert from "../Shared/FormAlert";
import PasswordRequirements from "../Shared/PasswordRequirements";
import { useAuthContext } from "../AuthContext";
import cognitoAuthService from "../../../services/cognitoAuthService";
import "../../../styles/auth.css";

const stripHtml = (str) => str.replace(/<[^>]*>/g, '');
const sanitizeText = (str) => stripHtml(str).trim();
const isValidEmail = (email) => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
const isValidPhone = (phone) => /^\+?[0-9\s\-().]{7,20}$/.test(phone);

const SignupForm = () => {
  const { t } = useTranslation();
  const { setCurrentView } = useAuthContext();

  const [signupStep, setSignupStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [confirmationComplete, setConfirmationComplete] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState("");
  const [passwordMismatch, setPasswordMismatch] = useState(false);
  const [clientCode, setClientCode] = useState("");
  const [hasIdp, setHasIdp] = useState(false);
  const [idpName, setIdpName] = useState("");
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    password: "",
    confirmPassword: "",
  });
  const [passwordValidation, setPasswordValidation] = useState({
    show: false,
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
  });

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const validatePasswordInput = (password) => {
    setPasswordValidation({
      show: password.length > 0,
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });
    updateField("password", password);
  };

  const handleValidateClient = async () => {
    if (!clientCode.trim()) {
      setError(t('signup.pleaseEnterClientCode'));
      return;
    }

    try {
      setLoading(true);
      setError("");
      const result = await cognitoAuthService.validateClient(clientCode.trim());
      setHasIdp(result.has_idp || false);
      setIdpName(result.idp_name || "");
      setSignupStep(2);
    } catch (err) {
      setError(err.message || t('signup.invalidClientCode'));
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async () => {
    const firstName = sanitizeText(form.firstName);
    const lastName = sanitizeText(form.lastName);
    const email = form.email.trim();
    const phoneNumber = form.phoneNumber.trim();

    if (!email || !form.password || !firstName || !lastName) {
      setError(t('signup.pleaseEnterAllFields'));
      return;
    }

    if (firstName.length > 50 || lastName.length > 50) {
      setError(t('signup.nameTooLong'));
      return;
    }

    if (!isValidEmail(email)) {
      setError(t('signup.invalidEmail'));
      return;
    }

    if (phoneNumber && !isValidPhone(phoneNumber)) {
      setError(t('signup.invalidPhone'));
      return;
    }

    if (form.password !== form.confirmPassword) {
      setPasswordMismatch(true);
      setError(t('signup.passwordsDoNotMatch'));
      return;
    }

    setPasswordMismatch(false);

    try {
      setLoading(true);
      setError("");
      await cognitoAuthService.signUp(
        clientCode.trim(),
        email,
        form.password,
        {
          firstName,
          lastName,
          phoneNumber,
        },
      );
      setSignupSuccess(true);
      setSuccess(t('signup.accountCreated'));
    } catch (err) {
      setError(err.message || t('signup.signUpFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSignup = async () => {
    if (!confirmationCode) {
      setError(t('signup.pleaseEnterConfirmationCode'));
      return;
    }

    try {
      setLoading(true);
      setError("");
      await cognitoAuthService.confirmSignUp(form.email, confirmationCode);
      setError("");
      setSuccess("");
      setConfirmationComplete(true);
    } catch (err) {
      setError(err.message || t('signup.confirmationFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setLoading(true);
      setError("");
      await cognitoAuthService.resendConfirmationCode(form.email);
      setSuccess(t('signup.confirmationCodeResent'));
    } catch (err) {
      setError(err.message || t('signup.failedToResendCode'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    setSignupStep(1);
    setError("");
    setSuccess("");
  };

  if (confirmationComplete) {
    return (
      <div className="form-container">
        <h3>{t('pendingApproval.title')}</h3>
        <div className="pending-approval-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
        </div>
        <p className="info-text">
          {t('pendingApproval.message')}
        </p>
        <div className="form-footer">
          <button
            type="button"
            onClick={() => setCurrentView("login")}
            className="link"
          >
            {t('pendingApproval.backToSignIn')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="form-container">
      <h3>{t('signup.title')}</h3>
      <FormAlert type="error" message={error} />
      <FormAlert type="success" message={success} />

      {signupStep === 1 && !signupSuccess && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleValidateClient();
          }}
        >
          <FormInput
            id="signup-client-code"
            value={clientCode}
            type="text"
            label={t('signup.clientCodeLabel')}
            placeholder={t('signup.clientCodePlaceholder')}
            disabled={loading}
            onChange={setClientCode}
          />
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? (
              <span className="spinner"></span>
            ) : (
              <span>{t('login.continueButton')}</span>
            )}
          </button>
        </form>
      )}

      {signupStep === 2 && !signupSuccess && (
        <>
          <button
            type="button"
            className="btn-back"
            onClick={handleGoBack}
            disabled={loading}
          >
            {t('signup.backButton')}
          </button>

          {hasIdp && (
            <>
              <button
                type="button"
                className="btn btn-primary"
                disabled={loading}
                onClick={async () => {
                  try {
                    setLoading(true);
                    setError("");
                    const appId = localStorage.getItem("auth_app_id") || "";
                    const externalRedirect = localStorage.getItem("auth_redirect") || "";
                    const result = await cognitoAuthService.getIdpAuthorizeUrl(clientCode.trim(), {
                      appId,
                      externalRedirect,
                    });
                    window.location.href = result.idp_url;
                  } catch (err) {
                    setError(err.message || "Failed to initiate SSO");
                    setLoading(false);
                  }
                }}
              >
                {loading ? (
                  <span className="spinner"></span>
                ) : (
                  <span>{t('signup.continueWithSso', { idpName })}</span>
                )}
              </button>
              <div className="or-divider">
                <span>{t('signup.orDivider')}</span>
              </div>
            </>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSignup();
            }}
          >

          <div className="form-row">
            <div className="form-group half-width">
              <FormInput
                id="signup-firstname"
                value={form.firstName}
                type="text"
                label={t('signup.firstNameLabel')}
                placeholder={t('signup.firstNamePlaceholder')}
                disabled={loading}
                maxLength={50}
                onChange={(value) => updateField("firstName", value)}
              />
            </div>
            <div className="form-group half-width">
              <FormInput
                id="signup-lastname"
                value={form.lastName}
                type="text"
                label={t('signup.lastNameLabel')}
                placeholder={t('signup.lastNamePlaceholder')}
                disabled={loading}
                maxLength={50}
                onChange={(value) => updateField("lastName", value)}
              />
            </div>
          </div>

          <FormInput
            id="signup-email"
            value={form.email}
            type="email"
            label={t('signup.emailLabel')}
            placeholder={t('signup.emailPlaceholder')}
            disabled={loading}
            maxLength={254}
            onChange={(value) => updateField("email", value)}
          />

          <FormInput
            id="signup-phone"
            value={form.phoneNumber}
            type="tel"
            label={t('signup.phoneLabel')}
            placeholder={t('signup.phonePlaceholder')}
            disabled={loading}
            maxLength={20}
            onChange={(value) => updateField("phoneNumber", value)}
          />

          <div className="form-group">
            <label htmlFor="signup-password">{t('login.passwordLabel')}</label>
            <input
              id="signup-password"
              value={form.password}
              type="password"
              className="form-control"
              placeholder={t('signup.passwordPlaceholder')}
              required
              disabled={loading}
              onChange={(e) => validatePasswordInput(e.target.value)}
            />
            <PasswordRequirements validation={passwordValidation} />
          </div>

          <FormInput
            id="signup-confirm-password"
            value={form.confirmPassword}
            type="password"
            label={t('signup.confirmPasswordLabel')}
            placeholder={t('signup.confirmPasswordPlaceholder')}
            disabled={loading}
            customClass={passwordMismatch ? "is-invalid" : ""}
            onChange={(value) => updateField("confirmPassword", value)}
          />
          {passwordMismatch && (
            <small className="error-text">{t('signup.passwordsDoNotMatch')}</small>
          )}

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? (
              <span className="spinner"></span>
            ) : (
              <span>{t('signup.createAccountButton')}</span>
            )}
          </button>
          </form>
        </>
      )}

      {signupSuccess && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleConfirmSignup();
          }}
        >
          <p className="info-text">
            {t('signup.confirmationInfo')}
          </p>
          <FormInput
            id="confirm-code"
            value={confirmationCode}
            type="text"
            label={t('signup.confirmationCodeLabel')}
            placeholder={t('signup.confirmationCodePlaceholder')}
            disabled={loading}
            onChange={setConfirmationCode}
          />
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? <span className="spinner"></span> : <span>{t('signup.confirmButton')}</span>}
          </button>
          <div className="form-footer">
            <button
              type="button"
              onClick={handleResendCode}
              className="link"
            >
              {t('signup.resendCode')}
            </button>
          </div>
        </form>
      )}

      {!signupSuccess && (
        <div className="form-footer">
          <button
            type="button"
            onClick={() => setCurrentView("login")}
            className="link"
          >
            {t('signup.alreadyHaveAccount')}
          </button>
        </div>
      )}
    </div>
  );
};

export default SignupForm;
